import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { cartService } from '@/features/cart/services/cartService';
import { GuestCheckoutErrorModal } from '@/features/cart/components/GuestCheckoutErrorModal';
import type { GuestCheckoutFormData } from '@/features/cart/types';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const GuestCheckoutConfirmationPage: React.FC = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [guestData, setGuestData] = useState<GuestCheckoutFormData | null>(null);
  const [confirmationNumber] = useState<string>(() => {
    // Generate a simple 6-digit confirmation number for testing
    return Math.floor(100000 + Math.random() * 900000).toString();
  });

  useEffect(() => {
    // Get guest data from location state
    if (location.state && 'guestData' in location.state) {
      setGuestData(location.state.guestData as GuestCheckoutFormData);
    } else {
      // If no guest data, redirect back to cart
      if (cartId) {
        navigate(`/carts/${cartId}`);
      } else {
        navigate('/carts');
      }
    }
  }, [location.state, navigate, cartId]);

  const handleConfirm = async () => {
    if (!cartId || !guestData) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Call guest checkout API
      await cartService.checkoutCartAsGuest(cartId, {
        email: guestData.email,
        first_name: guestData.firstName,
        last_name: guestData.lastName,
      });

      // Redirect to payment page
      navigate(`/carts/${cartId}/payment`);
    } catch (error) {
      console.error('Error during guest checkout:', error);
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      setError(errorMessage);
      setShowErrorModal(true);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!guestData) {
    return null; // Will redirect in useEffect
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-black">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gray-900 rounded-2xl border border-gray-700/50 p-8"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Confirm Your Information</h1>
              <p className="text-gray-400">
                Please review your information before proceeding to payment.
              </p>
            </div>

            {/* Confirmation Number */}
            <div className="bg-gray-800/50 rounded-lg p-6 mb-6 border border-gray-700/50">
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">Confirmation Number</p>
                <p className="text-3xl font-bold text-white tracking-wider">{confirmationNumber}</p>
                <p className="text-gray-500 text-xs mt-2">(For testing purposes)</p>
              </div>
            </div>

            {/* Guest Information */}
            <div className="space-y-4 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                <p className="text-white">{guestData.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">First Name</label>
                  <p className="text-white">{guestData.firstName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Last Name</label>
                  <p className="text-white">{guestData.lastName}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  if (cartId) {
                    navigate(`/carts/${cartId}`);
                  } else {
                    navigate('/carts');
                  }
                }}
                disabled={isProcessing}
                className="flex-1 bg-gray-800 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={isProcessing}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Confirm and Continue to Payment'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />

      {/* Error Modal */}
      <GuestCheckoutErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errorMessage={error || undefined}
      />
    </>
  );
};

export default GuestCheckoutConfirmationPage;

